import React, { useEffect, useCallback, useContext, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import { config } from '../../api/config';
import { actions } from '../../state';
import {
  formatFilename,
  formatMoney,
  isFilterActive,
  generateHeader,
  buildTableFilterBody,
  mapAffiliateInviteStatus,
  mapAffiliateInviteStatusColor,
} from '../../utils';
import {
  Header,
  HeaderActions,
  Title,
  Button,
  ButtonDownload,
  ButtonSimple,
  Table,
  TableRow,
  TableCell,
  SelectFilter,
  WithSpinner,
} from '../../components';
import { AffiliateContext } from './affiliate.context';

const PAGE_SIZE = config.DEFAULTS.recordsPerPage;

const defaultSortBody = {
  order: [{ by: 'id', order: 'desc' }],
};

const AffiliatePage = ({
  dispatch,
  affiliate: { affiliateInvites, total, header, loading },
  xlsLoading,
}) => {
  const {
    statusFilter,
    setStatusFilter,
    referrerPhoneFilter,
    setReferrerPhoneFilter,
    inviteePhoneFilter,
    setInviteePhoneFilter,
    sortOrder,
    setSortOrder,
    filterBody,
    setFilterBody,
  } = useContext(AffiliateContext);

  const [page, setPage] = useState(1);
  const pageCount = Math.ceil(total / PAGE_SIZE) || 1;

  const fetchAffiliateInvites = useCallback(
    body => {
      dispatch(actions.affiliate.getAffiliateInvitesAction(body));
    },
    [dispatch]
  );

  const clearAffiliateInvites = useCallback(() => {
    dispatch(actions.affiliate.clearAffiliateInvitesAction());
  }, [dispatch]);

  useEffect(() => () => clearAffiliateInvites(), [clearAffiliateInvites]);

  useEffect(() => {
    const offset = (page - 1) * PAGE_SIZE;
    const requestBody =
      filterBody && (filterBody.filter || filterBody.order)
        ? { ...filterBody, offset, limit: PAGE_SIZE }
        : { ...defaultSortBody, offset, limit: PAGE_SIZE };

    fetchAffiliateInvites(requestBody);
  }, [fetchAffiliateInvites, filterBody, page]);

  useEffect(() => {
    if (page > pageCount) {
      setPage(1);
    }
  }, [page, pageCount]);

  const handleSubmit = submitOptions => {
    const generatedFilter = [];

    if (statusFilter.length) {
      generatedFilter.push({ field: 'status', value: statusFilter });
    }
    if (referrerPhoneFilter.length) {
      generatedFilter.push({
        field: 'referrer_phone',
        value: referrerPhoneFilter,
      });
    }
    if (inviteePhoneFilter.length) {
      generatedFilter.push({
        field: 'invitee_phone',
        value: inviteePhoneFilter,
      });
    }

    setFilterBody(
      buildTableFilterBody({
        submitOptions,
        generatedFilter,
        sortOrder,
        defaultSortBody,
      })
    );
    setPage(1);
  };

  const clearFilters = () => {
    setStatusFilter([]);
    setReferrerPhoneFilter([]);
    setInviteePhoneFilter([]);
    handleSubmit({ clear: true });
    setPage(1);
  };

  const xlsExport = () => {
    const filename = formatFilename(config.PAGES.affiliate);
    dispatch(actions.affiliate.exportAffiliateInvitesAction(filename));
  };

  const tableHeader = [
    <SelectFilter
      title="Būsena"
      name="status"
      valueType="number"
      array={generateHeader(header.status, mapAffiliateInviteStatus)}
      filterState={statusFilter}
      setFilterState={setStatusFilter}
      onSubmit={handleSubmit}
      sortOrder={sortOrder}
      setSortOrder={setSortOrder}
      isActive={isFilterActive('select', statusFilter)}
    />,
    <SelectFilter
      title="Klientas"
      name="referrer_phone"
      valueType="string"
      array={generateHeader(header.referrer_phone)}
      filterState={referrerPhoneFilter}
      setFilterState={setReferrerPhoneFilter}
      onSubmit={handleSubmit}
      sortOrder={sortOrder}
      setSortOrder={setSortOrder}
      isActive={isFilterActive('select', referrerPhoneFilter)}
    />,
    <SelectFilter
      title="Pakviestasis"
      name="invitee_phone"
      valueType="string"
      array={generateHeader(header.invitee_phone)}
      filterState={inviteePhoneFilter}
      setFilterState={setInviteePhoneFilter}
      onSubmit={handleSubmit}
      sortOrder={sortOrder}
      setSortOrder={setSortOrder}
      isActive={isFilterActive('select', inviteePhoneFilter)}
    />,
    'Suma',
    'Liko',
    'Išviso kiek išleido',
  ];

  const renderTable = () =>
    affiliateInvites.map(
      ({
        id,
        status,
        referrer_phone,
        invitee_phone,
        required_spend_eur,
        remaining_eur,
        current_spend_eur,
      }) => (
        <TableRow key={id}>
          <TableCell>
            <span style={{ color: mapAffiliateInviteStatusColor(status) }}>
              {mapAffiliateInviteStatus(status)}
            </span>
          </TableCell>
          <TableCell>{referrer_phone}</TableCell>
          <TableCell>{invitee_phone}</TableCell>
          <TableCell>{formatMoney(required_spend_eur)}</TableCell>
          <TableCell>{formatMoney(remaining_eur)}</TableCell>
          <TableCell>{formatMoney(current_spend_eur)}</TableCell>
        </TableRow>
      )
    );

  const hasActiveFilters =
    !!filterBody ||
    !!statusFilter.length ||
    !!referrerPhoneFilter.length ||
    !!inviteePhoneFilter.length;

  return (
    <section>
      <Header>
        <Title total={total}>{config.PAGES.affiliate}</Title>
        <HeaderActions>
          {hasActiveFilters && (
            <ButtonSimple type="filter" onClick={clearFilters}>
              Išvalyti filtrus
            </ButtonSimple>
          )}
          <ButtonDownload onClick={xlsExport} loading={xlsLoading} />
          <Button color="primary" url={config.APP_PATHS.affiliateSettings}>
            Nustatymai
          </Button>
        </HeaderActions>
      </Header>

      <WithSpinner loading={loading}>
        <Table
          header={tableHeader}
          isEmpty={!affiliateInvites.length}
          loading={loading}
          filter={true}
          page={page}
          pageSize={PAGE_SIZE}
          total={total}
          serverPagination={true}
          onPageChange={setPage}
          rightAlignedFooterCells={[3, 4, 5]}
        >
          {renderTable()}
        </Table>
      </WithSpinner>
    </section>
  );
};

const mapStateToProps = state => ({
  affiliate: state.affiliate,
  xlsLoading: state.download.xlsLoading,
});

AffiliatePage.propTypes = {
  dispatch: PropTypes.func.isRequired,
  affiliate: PropTypes.shape({
    affiliateInvites: PropTypes.array,
    total: PropTypes.number,
    header: PropTypes.object,
    loading: PropTypes.bool,
  }).isRequired,
  xlsLoading: PropTypes.bool.isRequired,
};

export default connect(mapStateToProps)(AffiliatePage);
